import React from 'react';
import { motion } from 'framer-motion';
import { Trans } from 'react-i18next';
import { SunIcon, MoonIcon, GlobeAltIcon, Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { getLogoUrl } from '../utils/getLogoUrl';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface NavbarProps {
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (open: boolean) => void;
  i18n: any;
  scrollToSection: (sectionId: string) => void;
}

const Navbar: React.FC<NavbarProps> = ({ isMobileMenuOpen, setIsMobileMenuOpen, i18n, scrollToSection }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Helper to handle section navigation from any page
  const handleSectionNav = (section: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      navigate(`/?section=${section}`);
    } else {
      scrollToSection(section);
    }
  };

  return (
    <nav className="fixed w-full z-50 bg-white/80 dark:bg-black/80 backdrop-blur-lg">
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <img 
                src={getLogoUrl()} 
                alt="AgriPuente" 
                className="h-12 w-auto transition-transform dark:invert cursor-pointer" 
                onClick={() => navigate('/')}
              />
            </motion.div>
            <div className="hidden md:block">
              <h1 className="text-xl font-semibold bg-gradient-to-r from-accent to-emerald-600 bg-clip-text text-transparent">
                <Trans>tagline</Trans>
              </h1>
            </div>
          </div>
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <Link 
              to="/"
              onClick={handleSectionNav('services')}
              className="text-gray-800 dark:text-white hover:text-accent transition-colors"
            >
              <Trans>nav.services</Trans>
            </Link>
            <Link 
              to="/"
              onClick={handleSectionNav('team')}
              className="text-gray-800 dark:text-white hover:text-accent transition-colors"
            >
              <Trans>nav.team</Trans>
            </Link>
            <Link 
              to="/"
              onClick={handleSectionNav('request')}
              className="text-gray-800 dark:text-white hover:text-accent transition-colors"
            >
              <Trans>nav.request</Trans>
            </Link>
            <Link 
              to="/"
              onClick={handleSectionNav('contact')}
              className="text-gray-800 dark:text-white hover:text-accent transition-colors"
            >
              <Trans>nav.contact</Trans>
            </Link>
            <Link 
              to="/imports"
              className="text-gray-800 dark:text-white hover:text-accent transition-colors"
            >
              <Trans>nav.imports</Trans>
            </Link>
            <Link 
              to="/pricing"
              className={`text-gray-800 dark:text-white hover:text-accent transition-colors ${location.pathname === '/pricing' ? 'text-accent font-bold' : ''}`}
            >
              <Trans>nav.pricing</Trans>
            </Link>
          </div>
          {/* Theme and Language Controls */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => document.documentElement.classList.toggle('dark')}
              className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle theme"
            >
              {document.documentElement.classList.contains('dark') ? (
                <SunIcon className="h-5 w-5" />
              ) : (
                <MoonIcon className="h-5 w-5" />
              )}
            </button>
            <button
              onClick={() => {
                i18n.changeLanguage(i18n.language === 'en' ? 'es' : 'en');
              }}
              className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-accent dark:hover:text-accent transition-colors"
              aria-label="Toggle language"
            >
              <GlobeAltIcon className="w-5 h-5" />
              <span>{i18n.language === 'en' ? 'EN' : 'ES'}</span>
            </button>
            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle mobile menu"
            >
              {isMobileMenuOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
        {/* Mobile Navigation Menu */}
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{
            opacity: isMobileMenuOpen ? 1 : 0,
            height: isMobileMenuOpen ? 'auto' : 0
          }}
          transition={{ duration: 0.3 }}
          className="md:hidden overflow-hidden"
        >
          <div className="py-4 space-y-4">
            <Link 
              to="/?section=services"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full text-left px-4 py-2 text-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <Trans>nav.services</Trans>
            </Link>
            <Link 
              to="/?section=team"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full text-left px-4 py-2 text-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <Trans>nav.team</Trans>
            </Link>
            <Link 
              to="/?section=request"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full text-left px-4 py-2 text-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <Trans>nav.request</Trans>
            </Link>
            <Link 
              to="/?section=contact"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full text-left px-4 py-2 text-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <Trans>nav.contact</Trans>
            </Link>
            <Link 
              to="/imports"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full text-left px-4 py-2 text-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <Trans>nav.imports</Trans>
            </Link>
            <Link 
              to="/pricing"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block w-full text-left px-4 py-2 text-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors ${location.pathname === '/pricing' ? 'text-accent font-bold' : ''}`}
            >
              <Trans>nav.pricing</Trans>
            </Link>
          </div>
        </motion.div>
      </div>
    </nav>
  );
};

export default Navbar;
